import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslator } from '../components/translator/hook'
import HeaderMenu from '../components/headerMenu'
import MyProjectIcon from './myProjectIcon'
import './list.css'
import { getProjectList, ProjectInfo } from '../components/project/api'
import { LoadingScreen } from '../components/loading/loadingScreen'
import { ErrorModal } from '../components/modals/errorModal'
import { CourseDetailsList } from '../openapi'
import { NoProject } from '../components/projectList/noProject'
import { ProjectCard } from '../components/projectList/projectCard'
import { UserContext } from '../components/user/provider'

const List = () => {
  const { t } = useTranslator()
  const { siteName, getAllMissions, userId } = useContext(UserContext)

  const [projects, setProjects] = useState<ProjectInfo[]>([])
  const [error, setError] = useState<Error | null>(null)
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [allMissions, setAllMissions] = useState<
    CourseDetailsList | undefined
  >()

  const fetchProjectList = useCallback(async () => {
    if (!userId || !siteName) return
    setProjectsLoading(true)
    try {
      const [missions, projectList] = await Promise.all([
        getAllMissions(),
        getProjectList(userId),
      ])
      setProjects(projectList)
      setAllMissions(missions)
    } catch (error) {
      console.error('Failed to get project list:', error)
      setError(new Error(t('get_project_list_error')))
    } finally {
      setProjectsLoading(false)
    }
  }, [getAllMissions, siteName, t, userId])

  useEffect(() => {
    fetchProjectList()
  }, [fetchProjectList])

  const handleChanged = useCallback(() => {
    fetchProjectList()
  }, [fetchProjectList])

  const handleDeleted = useCallback((projectId: string) => {
    setProjects((prev) =>
      prev.filter((project) => project.projectId !== projectId),
    )
  }, [])

  const handleStopSharing = useCallback((projectId: string) => {
    setProjects((prev) =>
      prev.map((project) =>
        project.projectId === projectId ?
          { ...project, isPublic: false }
        : project,
      ),
    )
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return (
    <div className="box">
      <HeaderMenu type="list" />
      <div className="list-wrapper">
        <div className="title">
          <MyProjectIcon className="myproject-icon" />
          <h1 className="title-label">{t('project_list_title')}</h1>
        </div>

        {projectsLoading ?
          <LoadingScreen />
        : projects.length <= 0 ?
          <NoProject />
        : projects.map((project) => {
            const missionId = Number(project.missionId)

            const stage = allMissions?.courses
              ?.flatMap((c) => c.stages || [])
              .find((s) => s.missions.some((m) => m.id === missionId))
            const mission = stage?.missions.find((m) => m.id === missionId)

            return (
              <ProjectCard
                key={project.projectId}
                onChanged={handleChanged}
                onDeleted={handleDeleted}
                onError={setError}
                onStopSharing={handleStopSharing}
                project={project}
                siteName={siteName}
                stage={stage}
                mission={mission}
                userId={userId}
              />
            )
          })
        }
      </div>
      <ErrorModal
        error={error?.message || null}
        modalTitle={t('not_connected_error')}
        onClose={clearError}
      />
    </div>
  )
}

export default List
